import dayjs from "dayjs";
import { FaEye } from "react-icons/fa";

const STATUS_STYLE = {
  UNDER_TREATMENT: "bg-red-50 text-red-600 border border-red-200",
  "Under Treatment": "bg-red-50 text-red-600 border border-red-200",
  REGISTERED: "bg-blue-50 text-blue-600 border border-blue-200",
  Registered: "bg-blue-50 text-blue-600 border border-blue-200",
  OBSERVATION: "bg-amber-50 text-amber-700 border border-amber-200",
  Observation: "bg-amber-50 text-amber-700 border border-amber-200",
  STABLE: "bg-green-50 text-green-700 border border-green-200",
  Stable: "bg-green-50 text-green-700 border border-green-200",
  DISCHARGED: "bg-emerald-50 text-emerald-700 border border-emerald-200",
  Discharged: "bg-emerald-50 text-emerald-700 border border-emerald-200",
  CRITICAL: "bg-rose-50 text-rose-700 border border-rose-200",
  Critical: "bg-rose-50 text-rose-700 border border-rose-200",
  ADMITTED: "bg-indigo-50 text-indigo-700 border border-indigo-200",
  Admitted: "bg-indigo-50 text-indigo-700 border border-indigo-200",
};

const AVATAR_COLORS = [
  "#EF4444",
  "#F59E0B",
  "#6366F1",
  "#22C55E",
  "#8B5CF6",
  "#06B6D4",
  "#EC4899",
  "#14B8A6",
];

const getAvatarBg = (name) => {
  if (!name) return AVATAR_COLORS[0];
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
};

const formatAgeGender = (p) => {
  let age = p.age;
  if ((!age || age === 0 || age === "0") && p.dob) {
    const birthYear = dayjs(p.dob, ["MM/DD/YYYY", "YYYY-MM-DD", "DD/MM/YYYY"]);
    if (birthYear.isValid()) {
      age = dayjs().diff(birthYear, "year");
    }
  }
  const ageStr = age && age > 0 ? `${age} Y` : "";
  let genderStr = "";
  if (p.gender) {
    const g = String(p.gender).toUpperCase();
    if (g === "F" || g === "FEMALE") genderStr = "Female";
    else if (g === "M" || g === "MALE") genderStr = "Male";
    else genderStr = p.gender;
  }
  if (ageStr && genderStr) return `${ageStr} / ${genderStr}`;
  if (ageStr) return ageStr;
  if (genderStr) return genderStr;
  return p.ageGender || "-";
};

const formatArrivalTime = (p) => {
  if (p.arrivalDate) {
    const formattedDate = dayjs(p.arrivalDate).isValid()
      ? dayjs(p.arrivalDate).format("DD/MM/YYYY")
      : p.arrivalDate;
    let formattedTime = "";
    if (p.arrivalTime) {
      const timeObj = dayjs(`2000-01-01 ${p.arrivalTime}`);
      formattedTime = timeObj.isValid()
        ? timeObj.format("hh:mm A")
        : p.arrivalTime;
    }
    return formattedTime ? `${formattedDate}\n${formattedTime}` : formattedDate;
  }
  if (p.createdDate) {
    return dayjs(p.createdDate).isValid()
      ? dayjs(p.createdDate).format("DD/MM/YYYY\nhh:mm A")
      : p.createdDate;
  }
  return p.arrivalTime || "-";
};

const formatDoctor = (p) => {
  if (p.doctor) return p.doctor;
  if (p.doctorName) {
    return p.doctorName.startsWith("Dr.")
      ? p.doctorName
      : `Dr. ${p.doctorName}`;
  }
  if (p.doctorId) {
    return `Dr. ${p.doctorId}`;
  }
  return "Emergency Dept";
};

const formatDoctorRole = (p) => {
  return p.doctorRole || (p.doctorId ? `ID: ${p.doctorId}` : "Emergency");
};

const formatWardBed = (p) => {
  const ward = p.wardName || p.wardCode || "";
  const bed = p.bedType || p.bedCode || p.bedNumber || "";
  if (ward && bed) return `${ward} / ${bed}`;
  if (ward) return ward;
  if (bed) return `Bed ${bed}`;
  return p.wardBed || "-";
};

const TABLE_HEADERS = [
  { label: "Patient", className: "min-w-[200px]" },
  { label: "Mobile Number", className: "min-w-[140px]" },
  { label: "Age / Gender", className: "min-w-[120px]" },
  { label: "Chief Complaint", className: "min-w-[220px]" },
  { label: "Arrival Time", className: "min-w-[130px]" },
  { label: "Doctor", className: "min-w-[180px]" },
  { label: "Status", className: "min-w-[150px]" },
  { label: "Ward / Bed", className: "min-w-[130px]" },
  { label: "Actions", className: "w-20 text-center" },
];

export default function EmergencyTable({
  loading,
  paginatedList,
  filteredCount,
  currentPage,
  pageSize,
  totalPages,
  onPageChange,
  onPageSizeChange,
  onViewPatient,
}) {
  return (
    <div className="bg-white border rounded-xl shadow-sm overflow-hidden">
      <div className="overflow-x-auto custom-scrollbar">
        <table className="w-full min-w-[1280px] text-sm font-sans">
          <thead>
            <tr className="bg-gray-50 border-b">
              {TABLE_HEADERS.map((col) => (
                <th
                  key={col.label}
                  className={`px-4 py-3 text-xs font-semibold text-gray-500 whitespace-nowrap ${
                    col.className.includes("text-center") ? "text-center" : "text-left"
                  } ${col.className}`}
                >
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <tr key={i} className="border-b animate-pulse">
                  <td className="px-4 py-4 whitespace-nowrap text-center">
                    <div className="h-4 bg-gray-200 rounded w-6 mx-auto" />
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap min-w-[200px]">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-gray-200 flex-shrink-0" />
                      <div className="space-y-1">
                        <div className="h-3 bg-gray-200 rounded w-24" />
                        <div className="h-2 bg-gray-100 rounded w-16" />
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap min-w-[140px]">
                    <div className="h-3 bg-gray-200 rounded w-20" />
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap min-w-[120px]">
                    <div className="h-3 bg-gray-200 rounded w-16" />
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap min-w-[220px]">
                    <div className="h-3 bg-gray-200 rounded w-32" />
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap min-w-[130px]">
                    <div className="h-3 bg-gray-200 rounded w-20" />
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap min-w-[180px]">
                    <div className="h-3 bg-gray-200 rounded w-24" />
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap min-w-[150px]">
                    <div className="h-6 bg-gray-200 rounded w-20" />
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap min-w-[130px]">
                    <div className="h-3 bg-gray-200 rounded w-16" />
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-center w-20">
                    <div className="h-6 bg-gray-200 rounded w-8 mx-auto" />
                  </td>
                </tr>
              ))
            ) : paginatedList.length === 0 ? (
              <tr>
                <td
                  colSpan={TABLE_HEADERS.length}
                  className="px-4 py-10 text-center text-gray-400 text-sm"
                >
                  No emergency patients found.
                </td>
              </tr>
            ) : (
              paginatedList.map((p, idx) => {
                const patientName =
                  p.patientName || p.name || "Unknown Patient";
                const patientId =
                  p.emergencyId || p.patientId || `DZEMR${p.id || ""}`;
                const mobile = p.mobile || "-";
                const ageGender = formatAgeGender(p);
                const complaint = p.complaint || p.chiefComplaint || "-";
                const arrivalTime = formatArrivalTime(p);
                const doctorName = formatDoctor(p);
                const doctorRole = formatDoctorRole(p);
                const status = p.status || "REGISTERED";
                const statusClass =
                  STATUS_STYLE[status] ||
                  STATUS_STYLE[status.toUpperCase()] ||
                  "bg-gray-100 text-gray-700 border border-gray-200";
                const wardBed = formatWardBed(p);
                const avatarLetter = (
                  patientName.charAt(0) || "P"
                ).toUpperCase();
                const avatarBg = p.avatarBg || getAvatarBg(patientName);

                const triage = (p.triageLevel || "").toUpperCase();
                const triagePill =
                  triage === "HIGH"
                    ? "bg-red-100 text-red-700 border border-red-200"
                    : triage === "MEDIUM"
                      ? "bg-amber-100 text-amber-700 border border-amber-200"
                      : triage === "LOW"
                        ? "bg-green-100 text-green-700 border border-green-200"
                        : null;

                return (
                  <tr
                    key={p.emergencyId || p.id || idx}
                    className="border-b hover:bg-gray-50 transition-colors"
                  >
                   
                    <td className="px-4 py-3 whitespace-nowrap min-w-[200px]">
                      <div className="flex items-center gap-2.5">
                        <div
                          className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0"
                          style={{ backgroundColor: avatarBg }}
                        >
                          {avatarLetter}
                        </div>
                        <div>
                          <p className="font-semibold text-[#333333] text-xs">
                            {patientName}
                          </p>
                          <p className="text-[10px] text-gray-400">
                            {patientId}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-gray-600 whitespace-nowrap min-w-[140px]">
                      {mobile}
                    </td>
                    <td className="px-4 py-3 text-gray-600 whitespace-nowrap min-w-[120px]">
                      {ageGender}
                    </td>
                    <td
                      className="px-4 py-3 text-gray-600 min-w-[220px] max-w-[280px] truncate"
                      title={complaint}
                    >
                      {complaint}
                    </td>
                    <td className="px-4 py-3 text-gray-600 whitespace-pre text-xs leading-5 min-w-[130px]">
                      {arrivalTime}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap min-w-[180px]">
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 rounded-full bg-gray-200 flex items-center justify-center text-xs font-bold text-gray-600 flex-shrink-0">
                          {(
                            doctorName.replace(/^Dr\.\s*/i, "").charAt(0) || "D"
                          ).toUpperCase()}
                        </div>
                        <div>
                          <p className="text-xs font-semibold text-[#333333]">
                            {doctorName}
                          </p>
                          <p className="text-[10px] text-gray-400">
                            {doctorRole}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap min-w-[150px]">
                      <div className="flex flex-col gap-1 items-start">
                        <span
                          className={`px-2 py-0.5 rounded-md text-xs font-semibold ${statusClass}`}
                        >
                          {status.replace(/_/g, " ")}
                        </span>
                        {triagePill && (
                          <span
                            className={`text-[9px] px-1.5 py-0.2 rounded-full font-bold uppercase tracking-wider ${triagePill}`}
                          >
                            {triage} Priority
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-gray-600 font-medium whitespace-nowrap min-w-[130px]">
                      {wardBed}
                    </td>
                    <td className="px-4 py-3 text-center whitespace-nowrap w-20">
                      <button
                        onClick={() => onViewPatient(p)}
                        className="p-1.5 text-gray-500 bg-white hover:text-[#189FAA] hover:bg-teal-50 rounded-md transition inline-flex items-center justify-center"
                        title="View Emergency Details"
                      >
                        <FaEye className="w-4 h-4 text-[#189FAA]" />
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* ── Pagination ── */}
      <div className="px-4 py-3 border-t flex items-center justify-between flex-wrap gap-2">
        <p className="text-xs text-gray-500 font-sans">
          {/* Showing{" "}
          {filteredCount === 0 ? 0 : (currentPage - 1) * pageSize + 1} to{" "}
          {Math.min(currentPage * pageSize, filteredCount)} of {filteredCount}{" "}
          entries */}
        </p>
        <div className="flex items-center gap-2">
          <button
            onClick={() => onPageChange(Math.max(currentPage - 1, 1))}
            disabled={currentPage === 1}
            className="px-3 py-1 bg-white border rounded-md text-xs text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            ‹ Prev
          </button>
          <span className="text-xs text-gray-600 px-2">
            Page {currentPage} of {totalPages}
          </span>
          <button
            onClick={() => onPageChange(Math.min(currentPage + 1, totalPages))}
            disabled={currentPage >= totalPages}
            className="px-3 py-1 bg-white border rounded-md text-xs text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Next ›
          </button>
        </div>
      </div>
    </div>
  );
}
